import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnInit } from '@angular/core';
import { FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as cardValidation from 'creditcards';
import { DateTime } from 'luxon';
import { finalize } from 'rxjs/operators';
import { Case } from '../../../../../_base-shared/models/Case/Case';
import { PaymentCard } from '../../../../../_base-shared/models/Payment/PaymentCard';
import { PaymentRequest } from '../../../../../_base-shared/models/Payment/PaymentRequest';
import { PaymentTerm } from '../../../../../_base-shared/models/Payment/PaymentTerm';
import { Product } from '../../../../../_base-shared/models/Product';
import { User } from '../../../../../_base-shared/models/User/User';
import { CustomValidators } from '../../../../../_base-shared/validators/custom.validators';
import { ClientGlobalEventService } from '../../_shared/services/client-global-event.service';
import { ClientAppService } from '../../client-app.service';

@Component({
  selector:    'app-public-payment-handler-wrapper',
  templateUrl: './public-payment-handler-wrapper.component.html',
  styleUrls:   ['./public-payment-handler-wrapper.component.scss'],
  standalone:  false
})
export class PublicPaymentHandlerWrapperComponent implements OnInit {
  public isLoading                         = 0;
  public paymentRequest: PaymentRequest;
  public paymentRequestUuid: string | null = null;
  public case: Case;
  public caseUuid: string;
  public targetClient: User;
  public clientRole: 'client' | 'partner';
  public chargeAmount: number;
  public paymentCards: Array<PaymentCard>;
  public paymentPlanType: 'debt_plan' | 'additional_plans';
  public installment: PaymentTerm;

  public idCardForm: UntypedFormGroup;
  public submittingIdCard: boolean;
  public resolvingIdCardForm = 0;

  public paymentFormActive = false;

  public isSuccessful: boolean;
  public paymentResponse: string;

  private requestSignature: string;

  public desktopVersion: boolean;
  public errorMessage: string;
  public isIframe: boolean;

  private currentLanguage!: 'en' | 'es';

  constructor(
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private breakpointObserver: BreakpointObserver,
    private globalEventService: ClientGlobalEventService,
    private clientAppService: ClientAppService,
    private translate: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.Large, Breakpoints.XLarge]).subscribe({
      next: result => {
        this.desktopVersion = result.matches;
      }
    });
    this.route.data.subscribe(data => {
      this.isIframe = data.iframe;

      let requestChargeAmounts = +this.route.snapshot.queryParamMap?.get('amount');
      if (this.isIframe) {
        requestChargeAmounts = 0.01;
      }

      this.paymentRequestUuid = this.route.snapshot.queryParamMap?.get('payment_request');
      this.requestSignature   = decodeURI(this.route.snapshot.queryParamMap?.get('signature'));

      this.caseUuid        = this.route.snapshot.queryParamMap?.get('uuid');
      this.paymentPlanType = this.route.snapshot.queryParamMap?.get('plan_type') === 'additional_plans' ?
        'additional_plans' :
        'debt_plan';
      this.currentLanguage = this.route.snapshot.queryParamMap?.get('language') === 'en' ? 'en' : 'es';
      // this.translate.setFallbackLang(this.currentLanguage);

      if (this.paymentRequestUuid) {
        this.isLoading++;
        this.clientAppService.getPaymentRequest(this.paymentRequestUuid, this.requestSignature, ['case.product', 'case.client'])
          .pipe(finalize(() => this.isLoading--))
          .subscribe({
            next: result => {
              this.paymentRequest  = result.data;
              this.case            = this.paymentRequest.case;
              this.targetClient    = this.case.client;
              this.clientRole      = 'client';
              this.chargeAmount    = +this.paymentRequest.amount;
              this.paymentPlanType = this.paymentRequest.charge_additional_plan ? 'additional_plans' : 'debt_plan';
              this.setActiveBrand(this.case.product);
              this.fetchPaymentCards();
            }
          });
      } else if (this.caseUuid) {
        this.chargeAmount    = +requestChargeAmounts;
        this.paymentPlanType = this.route.snapshot.queryParamMap?.get('plan_type') === 'additional_plans' ?
          'additional_plans' :
          'debt_plan';
        this.buildIdCardForm(this.caseUuid); // Note: will not be able to see existing cards if commented out
      } else {
        console.log('poz', requestChargeAmounts);
        this.chargeAmount    = requestChargeAmounts ? +requestChargeAmounts : 50;
        this.paymentPlanType = this.route.snapshot.queryParamMap?.get('plan_type') === 'additional_plans' ?
          'additional_plans' :
          'debt_plan';
        this.buildIdCardForm(null);
      }
    });
  }

  private buildIdCardForm(caseUuid: string | null): void {
    this.idCardForm = this.fb.group({
      id_card: [null, [Validators.required, Validators.maxLength(13), CustomValidators.idCardPattern()]],
      // case_uuid: [caseUuid, [Validators.required]]
      case_uuid: [caseUuid]
    });
  }

  public submitIdCardForm(form: FormGroup): void {
    if (form.invalid) {
      form.markAllAsTouched();
      return;
    }

    const formData = form.value;

    if (this.case) {
      this.fetchPaymentCards();
    } else {
      this.submittingIdCard = true;
      this.clientAppService.getUserByIdCard(formData.id_card)
        .pipe(finalize(() => this.submittingIdCard = false))
        .subscribe({
          next:  result => {
            this.targetClient = result.data;
            this.resolvingIdCardForm++;
            this.clientAppService.getLatestUserCase(this.targetClient.uuid, ['client.address'])
              .pipe(finalize(() => this.resolvingIdCardForm--))
              .subscribe({
                next: res => {
                  this.case       = res.data;
                  this.clientRole = this.targetClient.id === this.case.partner_user_id ? 'partner' : 'client';
                  this.idCardForm = null;
                  this.clientAppService.paymentAmount(this.case.client.uuid, this.case.uuid).subscribe({
                    next: re => {
                      this.paymentPlanType = re.data.plan_type;
                      this.installment     = re.data.installment;
                      this.fetchPaymentCards();
                      let amountToCharge = 0;
                      if (this.isIframe) {
                        amountToCharge = 0.01;
                      } else {
                        if (this.installment) {
                          amountToCharge = this.installment.amount - this.installment.amount_paid;
                        }
                      }

                      this.chargeAmount = Math.max(amountToCharge ?? this.chargeAmount, 0);
                      this.idCardForm   = null;
                      this.buildNewCardForm(this.targetClient, this.chargeAmount);
                    }
                  });
                },
              });
          },
          error: () => {
            if (this.caseUuid) {
              this.isLoading++;
              this.clientAppService.showCase(formData.case_uuid, ['product']).pipe(finalize(() => this.isLoading--)).subscribe({
                next: result => {
                  this.case = result.data;
                  this.setActiveBrand(this.case.product);
                  this.targetClient = this.case.client;
                  this.idCardForm   = null;
                  this.buildNewCardForm(this.targetClient, this.chargeAmount);
                  // TODO: validate max charge amount
                  // this.clientAppService.paymentAmount(this.case.client.uuid, this.case.uuid).subscribe(
                  //   res => {
                  //     this.paymentPlanType = res.data.plan_type;
                  //     this.installment = res.data.installment;
                  //     let amountToCharge = 0;
                  //     if (this.installment) {
                  //       amountToCharge = this.installment.amount - this.installment.amount_paid;
                  //     }
                  //     this.chargeAmount = amountToCharge;
                  //     this.idCardForm = null;
                  //     this.buildNewCardForm(this.targetClient, this.chargeAmount);
                  //   });
                }
              });
            } else {
              this.errorMessage = 'Invalid DNI';
            }
          }
        });
    }
  }

  private fetchPaymentCards(): void {
    const requestData = this.paymentRequest ?
      {signature: this.requestSignature, id_card: this.targetClient.id_card} :
      null;

    const observable = this.paymentRequest && this.targetClient.id_card ?
      this.clientAppService.authorizePaymentRequest(this.paymentRequest.uuid, requestData) :
      this.clientAppService.getPaymentCardsPublic(this.case.uuid);

    this.submittingIdCard = true;
    observable.pipe(finalize(() => this.submittingIdCard = false))
      .subscribe({
        next: result => {
          this.paymentCards      = this.mapPaymentCards(result.data);
          this.idCardForm        = null;
          this.paymentFormActive = true;
        }
      });
  }

  public dismissError(): void {
    this.errorMessage = null;
  }

  private buildNewCardForm(targetClient: User, chargeAmount: number): void {
    this.paymentFormActive = true;
  }

  private mapPaymentCards(paymentCards: Array<PaymentCard>): Array<PaymentCard> {
    return paymentCards.map(card => {
      const expDateObj         = DateTime.fromFormat(
        `${card.card_exp_month}${card.card_exp_year}`,
        'Myyyy'
      ).startOf('month');
      card.expires_at          = expDateObj.endOf('month').toISO();
      const expirationDateDiff = expDateObj.diffNow('months').months;

      if (expirationDateDiff <= 4) {
        card.expire = 'soon';
      }

      if (expirationDateDiff < 0) {
        card.expire = 'expired';
      }

      card.card_brand = card.card_brand ? card.card_brand : cardValidation.card.type(card.card_bin);

      return card;
    });
  }

  private setActiveBrand(product: Product) {
    this.globalEventService.setActiveBrand('unifye');
  }
}
